import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCreateTopic } from '@/hooks/useForum';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useSpamProtection } from '@/hooks/useSpamProtection';
import { X, Send, LogIn } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Category {
  id: number;
  name: string;
  parent_id: number | null;
}

interface ForumNewTopicFormProps {
  categories: Category[]; // Subcategories only
  onClose: () => void;
  onSuccess: (topicId: number) => void;
  parentCategories?: Category[]; // Parent categories for grouping
}

export function ForumNewTopicForm({ categories, onClose, onSuccess, parentCategories }: ForumNewTopicFormProps) {
  const [categoryId, setCategoryId] = useState<string>('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  const { toast } = useToast();
  const createTopic = useCreateTopic();
  const { user, profile, isAuthenticated } = useAuth();
  const { isSpam, HoneypotField } = useSpamProtection();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isAuthenticated || !profile) {
      toast({
        title: 'Du måste vara inloggad',
        description: 'Logga in för att skapa trådar.',
        variant: 'destructive',
      });
      return;
    }

    // Check for spam/bot behavior
    if (isSpam()) {
      toast({
        title: 'Kunde inte skicka',
        description: 'Vänta några sekunder och försök igen.',
        variant: 'destructive',
      });
      return;
    }

    if (!categoryId || !title.trim() || !content.trim()) {
      toast({
        title: 'Fyll i alla fält',
        description: 'Alla fält måste fyllas i för att skapa en tråd.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await createTopic.mutateAsync({
        categoryId: parseInt(categoryId),
        title: title.trim(),
        content: content.trim(),
        authorName: profile.display_name,
      });

      toast({
        title: 'Tråd skapad!',
        description: 'Din tråd har publicerats.',
      });

      onSuccess(result.topicId);
    } catch (error) {
      toast({
        title: 'Fel',
        description: 'Kunde inte skapa tråd. Försök igen.',
        variant: 'destructive',
      });
    }
  };

  if (!isAuthenticated) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <LogIn className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-display text-lg font-medium mb-2">Logga in för att skapa trådar</h3>
          <p className="text-muted-foreground mb-4">
            Du måste ha ett konto för att kunna delta i diskussioner.
          </p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={onClose}>
              Avbryt
            </Button>
            <Link to="/auth">
              <Button className="gap-2">
                <LogIn className="h-4 w-4" />
                Logga in
              </Button>
            </Link>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl font-semibold">Ny tråd</h2>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Honeypot field - invisible to humans, bots will fill it */}
        <HoneypotField />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Ditt namn</Label>
            <Input
              value={profile?.display_name || ''}
              disabled
              className="bg-muted"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="category">Kategori</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger>
                <SelectValue placeholder="Välj kategori..." />
              </SelectTrigger>
              <SelectContent>
                {parentCategories && parentCategories.length > 0 ? (
                  // Group subcategories by parent
                  parentCategories.map((parent) => {
                    const subs = categories.filter(c => c.parent_id === parent.id);
                    if (subs.length === 0) return null;
                    return (
                      <div key={parent.id}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                          {parent.name}
                        </div>
                        {subs.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id.toString()}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </div>
                    );
                  })
                ) : (
                  // Fallback: just list categories
                  categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>
                      {cat.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="title">Rubrik</Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Skriv en rubrik..."
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="content">Meddelande</Label>
          <Textarea
            id="content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Skriv ditt meddelande..."
            rows={6}
            required
          />
        </div>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose}>
            Avbryt
          </Button>
          <Button type="submit" disabled={createTopic.isPending} className="gap-2">
            <Send className="h-4 w-4" />
            {createTopic.isPending ? 'Skapar...' : 'Skapa tråd'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
